import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Spinner } from '../../components/ui/Spinner';
import { Button } from '../../components/ui/button';
import { ApiError } from '../../lib/api';
import { linkifyText } from '../../lib/linkifyText';
import { formatRelativeTime } from '../../lib/relativeTime';
import { formatUserDisplayName } from '../../lib/formatUserDisplayName';
import { useCurrentUser } from '../auth';
import type { CurrentUser } from '../../types/user';
import type { PromotionPlanThreadEntry } from '../../types/promotionPlanThread';
import {
  deleteComment,
  editComment,
  listThread,
  listUsers,
  postComment,
} from './api';

// Slice B §7 thread surface. A scrolling Section on Plan detail (decision
// #1 — section, not a tab). Reverse-chronological (server sorts); free
// comments + auto-linked task notes; compose at top; edit/soft-delete own
// comments. URL auto-linkification via the shared linkifyText.
export function PlanThread({ planId }: { planId: string }) {
  const qc = useQueryClient();
  const me = useCurrentUser();
  const [draft, setDraft] = useState('');
  const [error, setError] = useState<string | null>(null);

  const threadQuery = useQuery({
    queryKey: ['plan', planId, 'thread'],
    queryFn: () => listThread(planId),
    refetchOnWindowFocus: true,
  });
  const usersQuery = useQuery({
    queryKey: ['users', 'all'],
    queryFn: listUsers,
  });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['plan', planId, 'thread'] });
    qc.invalidateQueries({ queryKey: ['plan', planId, 'activity'] });
  };

  const postMut = useMutation({
    mutationFn: () => postComment(planId, draft.trim()),
    onSuccess: () => {
      setDraft('');
      invalidate();
    },
    onError: (err) =>
      setError(err instanceof ApiError ? err.message : String(err)),
  });

  const users = usersQuery.data ?? [];
  const nameFor = (uid: string): string => {
    const u = users.find((x) => x.uid === uid);
    return u ? formatUserDisplayName(u, { full: true }) : 'Someone';
  };

  return (
    <div>
      <form
        className="mb-5"
        onSubmit={(e) => {
          e.preventDefault();
          if (!draft.trim()) return;
          setError(null);
          postMut.mutate();
        }}
      >
        <textarea
          value={draft}
          rows={3}
          placeholder="Type a comment…"
          onChange={(e) => setDraft(e.target.value)}
          className="w-full rounded border border-slate-300 px-3 py-2 text-sm"
        />
        <div className="mt-2 flex items-center justify-between">
          {error ? (
            <p className="text-sm text-red-600">{error}</p>
          ) : (
            <span />
          )}
          <Button
            type="submit"
            disabled={!draft.trim() || postMut.isPending}
          >
            {postMut.isPending ? 'Posting…' : 'Post'}
          </Button>
        </div>
      </form>

      {threadQuery.isLoading ? (
        <Spinner />
      ) : threadQuery.error ? (
        <p className="text-sm text-red-600">
          Failed to load thread: {(threadQuery.error as Error).message}
        </p>
      ) : !threadQuery.data || threadQuery.data.length === 0 ? (
        <p className="text-sm italic text-slate-400">
          No comments yet. Start the conversation above.
        </p>
      ) : (
        <ul className="space-y-4">
          {threadQuery.data.map((entry) => (
            <ThreadRow
              key={entry.id}
              planId={planId}
              entry={entry}
              authorName={nameFor(entry.createdBy)}
              me={me}
              onChanged={invalidate}
            />
          ))}
        </ul>
      )}
    </div>
  );
}

function ThreadRow({
  planId,
  entry,
  authorName,
  me,
  onChanged,
}: {
  planId: string;
  entry: PromotionPlanThreadEntry;
  authorName: string;
  me: CurrentUser;
  onChanged: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [editDraft, setEditDraft] = useState(entry.content ?? '');
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const editMut = useMutation({
    mutationFn: () => editComment(planId, entry.id, editDraft.trim()),
    onSuccess: () => {
      setEditing(false);
      onChanged();
    },
    onError: (err) =>
      setError(err instanceof ApiError ? err.message : String(err)),
  });
  const deleteMut = useMutation({
    mutationFn: () => deleteComment(planId, entry.id),
    onSuccess: () => {
      setConfirmDelete(false);
      onChanged();
    },
    onError: (err) =>
      setError(err instanceof ApiError ? err.message : String(err)),
  });

  const isDeleted = !!entry.deletedAt;
  const isTaskNote = entry.type === 'task_note';
  // Author-only affordances (§7.6) — task notes are never editable from
  // the thread (edit the task's notes instead).
  const canManage =
    !isTaskNote && !isDeleted && entry.createdBy === me.uid;
  const body = isTaskNote ? entry.noteSnapshot : entry.content;

  return (
    <li className="rounded-md border border-slate-200 bg-white p-3">
      <div className="mb-1 flex items-center justify-between gap-2">
        <span className="text-sm font-medium text-slate-800">
          {authorName}
          {isTaskNote && (
            <span className="ml-2 text-xs font-normal text-slate-500">
              · note on “{entry.taskTitleSnapshot ?? 'a task'}”
            </span>
          )}
        </span>
        <time
          dateTime={entry.createdAt}
          title={new Date(entry.createdAt).toLocaleString()}
          className="text-xs text-slate-400"
        >
          {formatRelativeTime(entry.createdAt)}
        </time>
      </div>

      {isDeleted ? (
        <p className="text-sm italic text-slate-400">
          Comment deleted by author
        </p>
      ) : editing ? (
        <div>
          <textarea
            value={editDraft}
            rows={3}
            onChange={(e) => setEditDraft(e.target.value)}
            className="w-full rounded border border-slate-300 px-2 py-1 text-sm"
          />
          <div className="mt-2 flex justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setEditing(false);
                setEditDraft(entry.content ?? '');
              }}
              disabled={editMut.isPending}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={() => {
                if (editDraft.trim()) editMut.mutate();
              }}
              disabled={!editDraft.trim() || editMut.isPending}
            >
              {editMut.isPending ? 'Saving…' : 'Save'}
            </Button>
          </div>
        </div>
      ) : (
        <p className="whitespace-pre-wrap text-sm text-slate-700">
          {linkifyText(body ?? '')}
        </p>
      )}

      {entry.updatedAt && !isDeleted && !isTaskNote && !editing && (
        <p className="mt-1 text-[11px] text-slate-400">edited</p>
      )}

      {canManage && !editing && (
        <div className="mt-2 flex gap-3 text-xs">
          <button
            type="button"
            onClick={() => {
              setError(null);
              setEditDraft(entry.content ?? '');
              setEditing(true);
            }}
            className="text-slate-500 hover:text-slate-800"
          >
            Edit
          </button>
          <button
            type="button"
            onClick={() => {
              setError(null);
              setConfirmDelete(true);
            }}
            className="text-red-600 hover:text-red-700"
          >
            Delete
          </button>
        </div>
      )}

      {error && <p className="mt-1 text-xs text-red-600">{error}</p>}

      {confirmDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-sm rounded-md border border-slate-200 bg-white p-5 shadow-xl">
            <h3 className="text-sm font-semibold text-slate-900">
              Delete comment
            </h3>
            <p className="mt-2 text-sm text-slate-600">
              This replaces the comment with “Comment deleted by author”.
              It can't be undone.
            </p>
            <div className="mt-4 flex justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setConfirmDelete(false)}
                disabled={deleteMut.isPending}
              >
                Cancel
              </Button>
              <Button
                type="button"
                onClick={() => deleteMut.mutate()}
                disabled={deleteMut.isPending}
                variant="destructive"
              >
                {deleteMut.isPending ? 'Deleting…' : 'Delete'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </li>
  );
}
